(() => {
  const cleanups = new Map();
  const listeners = controller => (target, type, callback, options = {}) => target.addEventListener(type, callback, { ...options, signal: controller.signal });
  const initMenu = menu => {
    if (cleanups.has(menu)) return;
    const controller = new AbortController();
    const listen = listeners(controller);
    const button = menu.querySelector('.mugshot-menu-toggle');
    const panel = menu.querySelector('nav');
    const setOpen = (open, restoreFocus = false) => {
      button.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      if (restoreFocus) button.focus();
    };
    listen(button, 'click', () => setOpen(panel.hidden));
    listen(button, 'keydown', event => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setOpen(true);
        panel.querySelector('a').focus();
      }
    });
    listen(document, 'pointerdown', event => {
      if (!menu.contains(event.target) && !panel.hidden) setOpen(false);
    });
    listen(document, 'focusin', event => {
      if (!menu.contains(event.target) && !panel.hidden) setOpen(false);
    });
    listen(document, 'keydown', event => {
      if (event.key === 'Escape' && !panel.hidden) {
        event.preventDefault();
        setOpen(false, true);
      }
    });
    listen(panel, 'click', event => {
      if (event.target.closest('a')) setOpen(false);
    });
    cleanups.set(menu, () => { controller.abort(); setOpen(false); });
  };
  const initCarousel = section => {
    if (cleanups.has(section)) return;
    const controller = new AbortController();
    const listen = listeners(controller);
  const viewport = section.querySelector('.mugshot-lineup-viewport');
  const track = section.querySelector('.mugshot-lineup-list');
  if (!viewport || !track) return;
  const originals = [...track.children];
  const count = originals.length;
  if (count < 2) return;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const clone = figure => {
    const copy = figure.cloneNode(true);
    copy.setAttribute('aria-hidden', 'true');
    copy.dataset.carouselClone = '';
    copy.querySelectorAll('img').forEach(img => { img.alt = ''; img.draggable = false; });
    return copy;
  };
  track.prepend(...originals.map(clone));
  track.append(...originals.map(clone));
  originals.forEach(figure => { figure.querySelector('img').draggable = false; });
  section.dataset.carouselReady = '';
  viewport.tabIndex = 0;
  viewport.setAttribute('role', 'group');

  let step = 0, current = 0, moving = false, settleTimer;
  let touching = false, nextAdvance = performance.now() + 2000;
  const hoverCapable = matchMedia('(any-hover: hover)');
  let hovering = false;
  const deferAdvance = () => { nextAdvance = performance.now() + 2000; };
  const settle = () => {
    clearTimeout(settleTimer);
    if (!step || touching || hovering) return;
    current = ((Math.round(viewport.scrollLeft / step) % count) + count) % count;
    const target = (count + current) * step;
    if (Math.abs(viewport.scrollLeft - target) > 1) viewport.scrollTo({ left: target, behavior: 'instant' });
    moving = false;
    deferAdvance();
  };
  const move = direction => {
    if (!step || moving) return;
    deferAdvance();
    moving = true;
    viewport.scrollTo({ left: viewport.scrollLeft + direction * step, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    clearTimeout(settleTimer);
    settleTimer = setTimeout(settle, 900);
  };
  const pauseForHover = () => {
    if (!hoverCapable.matches) return;
    hovering = true;
    viewport.dataset.carouselHovered = '';
    clearTimeout(settleTimer);
    viewport.scrollTo({ left: viewport.scrollLeft, behavior: 'instant' });
    moving = false;
    deferAdvance();
  };
  const resumeAfterHover = () => {
    hovering = false;
    delete viewport.dataset.carouselHovered;
    deferAdvance();
  };
  [...track.children].forEach(figure => {
    listen(figure, 'pointerenter', event => { if (event.pointerType !== 'touch') pauseForHover(); });
    listen(figure, 'pointerleave', resumeAfterHover);
  });
  listen(reducedMotion, 'change', deferAdvance);
  const resize = () => {
    const nextStep = originals[0].getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap);
    if (Math.abs(nextStep - step) < 0.1) return;
    clearTimeout(settleTimer);
    step = nextStep;
    viewport.scrollTo({ left: (count + current) * step, behavior: 'instant' });
    moving = false;
    deferAdvance();
  };
  listen(viewport, 'scroll', () => {
    deferAdvance();
    clearTimeout(settleTimer);
    settleTimer = setTimeout(settle, 160);
  }, { passive: true });
  listen(viewport, 'scrollend', settle);
  listen(viewport, 'keydown', event => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      move(event.key === 'ArrowLeft' ? -1 : 1);
    }
  });
  listen(viewport, 'pointerenter', deferAdvance);
  listen(viewport, 'pointerleave', deferAdvance);
  listen(viewport, 'pointerdown', () => { touching = true; deferAdvance(); });
  const release = () => { if (touching) { touching = false; clearTimeout(settleTimer); settleTimer = setTimeout(settle, 160); } };
  listen(window, 'pointerup', release);
  listen(window, 'pointercancel', release);
  listen(window, 'blur', release);
  listen(viewport, 'lostpointercapture', release);
  listen(document, 'visibilitychange', () => { release(); deferAdvance(); });
  const clock = setInterval(() => {
    const bounds = viewport.getBoundingClientRect();
    const offscreen = bounds.bottom <= 0 || bounds.top >= innerHeight;
    if (reducedMotion.matches || document.hidden || offscreen || touching || hovering || viewport.matches(':focus-visible')) {
      deferAdvance();
      return;
    }
    if (!moving && performance.now() >= nextAdvance) move(1);
  }, 250);
  const observer = new ResizeObserver(resize);
  observer.observe(viewport);
  resize();
    cleanups.set(section, () => {
      controller.abort();
      clearInterval(clock);
      clearTimeout(settleTimer);
      observer.disconnect();
      track.querySelectorAll('[data-carousel-clone]').forEach(element => element.remove());
      delete section.dataset.carouselReady;
      delete viewport.dataset.carouselHovered;
    });
  };
  const init = root => {
    root.querySelectorAll('.mugshot-menu').forEach(initMenu);
    root.querySelectorAll('.mugshot-lineup').forEach(initCarousel);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [element, cleanup] of cleanups) {
      if (event.target.contains(element)) { cleanup(); cleanups.delete(element); }
    }
  });
})();
